import {
  ApeironBlueButton,
  DEFAULT_ANIMATE_DURATION,
  MayNull,
  NextImage
} from '@apeiron/temp'
import { Box, ButtonBase, InputBase, styled } from '@mui/material'
import EditSVG from '@public/icons/icon-edit.svg'
import useOnClickOutside from '@src/hooks/common/ui/useOnClickOutside'
import {
  ChangeEvent,
  Dispatch,
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  align-items: center;
  display: flex;
`

const InputContainer = styled(Box)`
  background-color: #151615;
  border-radius: 2px;
  border: 1px solid #151615;
  display: flex;
  flex: 1;
  height: 30px;
  justify-content: space-between;
  padding-left: 10px;
  padding-right: 8px;
  :focus-within {
    border: 1px solid #ffc937;
  }
`

const Input = styled(InputBase)`
  background-color: #151615;
  color: #ffffff;
  font-size: 20px;
  height: 28px;
  width: 100%;
  & .Mui-disabled.Mui-disabled {
    color: #ffffff;
    -webkit-text-fill-color: #ffffff;
  }
`

const SaveButton = styled(ApeironBlueButton)`
  border-radius: 2px;
  height: 30px;
  margin-left: 12px;
  width: 100px;
`

const EditIcon = styled(NextImage)`
  aspect-ratio: 1;
  margin-right: 6px;
  height: 75%;
  transition: stroke ${DEFAULT_ANIMATE_DURATION}ms,
    fill ${DEFAULT_ANIMATE_DURATION}ms;
  filter: invert(45%);
  :hover {
    filter: invert(83%);
  }
  :active {
    filter: invert(88%);
  }
`

const ApeironNameInput: FC<Props> = props => {
  const {
    buttonPlaceholder,
    canEdit,
    defaultName,
    isEditing,
    maxLength = 255,
    onSave,
    setIsEditing
  } = props

  const [inputElement, setInputElement] = useState<MayNull<HTMLElement>>(null)

  const [name, setName] = useState<string>('')

  const { t } = useTranslation()

  const handleOnSave = useCallback(() => {
    setIsEditing(false)
    onSave(name)
  }, [name, onSave, setIsEditing])

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }, [])

  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleOnSave()
      }
    },
    [handleOnSave]
  )

  const handleOnEdit = useCallback(
    (event: MouseEvent) => {
      // Prevent removed button trigger click outside
      event.stopPropagation()

      ReactDOM.flushSync(() => setIsEditing(true))
      inputElement?.focus()
    },
    [inputElement, setIsEditing]
  )

  const handleOnBlur = () => {
    setIsEditing(false)
    setName(defaultName)
  }

  useEffect(() => {
    setName(defaultName)
  }, [defaultName])

  useOnClickOutside(inputElement, handleOnBlur)

  return (
    <Container>
      <InputContainer>
        <Input
          value={name}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          disabled={!isEditing}
          inputRef={setInputElement}
          inputProps={{ maxLength, 'aria-label': 'name' }}
        />
        {canEdit && !isEditing && (
          <ButtonBase onClick={handleOnEdit}>
            <EditIcon src={EditSVG} />
          </ButtonBase>
        )}
      </InputContainer>

      {isEditing ? (
        <SaveButton onClick={handleOnSave}>
          {t('common.button.save')}
        </SaveButton>
      ) : (
        buttonPlaceholder
      )}
    </Container>
  )
}

type Props = {
  buttonPlaceholder: ReactNode
  canEdit: boolean
  defaultName: string
  isEditing: boolean
  maxLength?: number
  onSave: (name: string) => void
  setIsEditing: Dispatch<SetStateAction<boolean>>
}

export default ApeironNameInput
